import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './aboutus.css';

// Reusable component for animated elements on scroll
function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function AboutUs() {
  const foundingYear = 2021;
  const currentYear = new Date().getFullYear();

  // Data
  const values = [
    {
      title: "Innovation",
      description: "We challenge the status quo and explore cutting-edge technologies to build what's next, not just what's now.",
      icon: "💡"
    },
    {
      title: "Collaboration",
      description: "Great ideas are born from diverse perspectives. We work closely with our clients as partners, not just vendors.",
      icon: "🤝"
    },
    {
      title: "Impact",
      description: "We measure our success by the tangible value and positive change we deliver to our clients' businesses.",
      icon: "🎯"
    },
    {
      title: "Excellence",
      description: "We're committed to delivering superior quality in everything we do, from code to customer service.",
      icon: "⭐"
    },
  ];

  const milestones = [
    { year: 2021, event: "LEARNEX Founded", description: "Established with a vision to revolutionize IT solutions through innovation." },
    { year: 2022, event: "First Enterprise Client", description: "Secured partnership with a Fortune 500 company, validating our approach." },
    { year: 2023, event: "Team Expansion", description: "Grew our team to over 50 technology experts and industry specialists." },
    { year: 2024, event: "AI & Cloud Division", description: "Launched specialized practices in AI, Machine Learning and Cloud solutions." },
  ];

  const services = [
    { name: "Custom Software Development", icon: "🖥️" },
    { name: "Cloud Solutions & Migration", icon: "☁️" },
    { name: "AI & Machine Learning", icon: "🤖" },
    { name: "Cybersecurity Services", icon: "🔒" },
    { name: "IT Consulting", icon: "📊" },
    { name: "Digital Transformation", icon: "🔄" },
  ];

  return (
    <div className="aboutus-container">
      {/* ===== HERO HEADER ===== */}
      <section className="about-hero">
        <motion.div 
          className="about-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="company-logo">
            <h1>LEARNEX</h1>
            <p>Intelligent Technology Solutions</p>
          </div>
          <h2>Transforming Businesses Through <span className="gradient-text">Innovative Technology</span></h2>
          <p className="hero-subtitle">
            Since {foundingYear}, LEARNEX has been at the forefront of delivering cutting-edge IT solutions that drive growth, efficiency, and competitive advantage for businesses worldwide. Our team of {currentYear - foundingYear > 2 ? '50+' : 'highly skilled'} experts combines technical excellence with deep industry knowledge to create digital solutions that matter.
          </p>
        </motion.div>
        <motion.div 
          className="hero-graphic"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="tech-graphic">
            <div className="floating-element element-1">AI</div>
            <div className="floating-element element-2">Cloud</div>
            <div className="floating-element element-3">Data</div>
            <div className="floating-element element-4">IoT</div>
            <div className="main-circle">
              <div className="inner-circle"></div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ===== VISION & MISSION SECTION ===== */}
      <section className="vision-mission-section">
        <div className="vm-container">
          <AnimatedSection className="vision-card vm-card">
            <div className="vm-icon">👁️</div>
            <h3>Our Vision</h3>
            <p>"To inspire innovation and lead the way in creating smarter IT solutions for a better tomorrow."</p>
          </AnimatedSection>
          
          <AnimatedSection className="mission-card vm-card">
            <div className="vm-icon">🎯</div>
            <h3>Our Mission</h3>
            <p>"Delivering reliable, innovative, and cost-effective IT solutions that empower businesses and communities."</p>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="services-section">
        <AnimatedSection>
          <h2>Our Expertise</h2>
          <p className="section-subtitle">Comprehensive IT solutions tailored to your business needs</p>
        </AnimatedSection>
        <div className="services-grid">
          {services.map((service, index) => (
            <AnimatedSection key={index} className="service-item">
              <motion.div 
                className="service-icon"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {service.icon}
              </motion.div>
              <h4>{service.name}</h4>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ===== VALUES SECTION ===== */}
      <section className="values-section">
        <AnimatedSection>
          <h2>Our Core Values</h2>
          <p className="section-subtitle">The principles that guide everything we do</p>
        </AnimatedSection>
        <div className="values-grid">
          {values.map((value, index) => (
            <AnimatedSection key={index} className="value-card">
              <motion.div 
                className="value-icon"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {value.icon}
              </motion.div>
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ===== TIMELINE SECTION ===== */}
      <section className="timeline-section">
        <AnimatedSection>
          <h2>Our Journey</h2>
          <p className="section-subtitle">Key milestones in our growth story</p>
        </AnimatedSection>
        <div className="timeline">
          {milestones.map((milestone, index) => (
            <AnimatedSection key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
              <div className="timeline-content">
                <motion.div 
                  className="timeline-year"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
                >
                  {milestone.year}
                </motion.div>
                <h4>{milestone.event}</h4>
                <p>{milestone.description}</p>
              </div>
              <div className="timeline-marker"></div>
            </AnimatedSection>
          ))}
          <div className="timeline-line"></div>
        </div>
      </section>

      {/* ===== TEAM SECTION ===== */}
      <section className="team-section">
        <AnimatedSection>
          <h2>Our Leadership</h2>
          <p className="section-subtitle">Experienced professionals guiding our vision</p>
        </AnimatedSection>
        <div className="team-grid">
          <AnimatedSection className="team-member">
            <div className="member-photo"></div>
            <h4>Sarah Johnson</h4>
            <p className="role">CEO & Founder</p>
            <p>15+ years in technology leadership</p>
          </AnimatedSection>
          <AnimatedSection className="team-member">
            <div className="member-photo"></div>
            <h4>Michael Chen</h4>
            <p className="role">CTO</p>
            <p>Expert in AI and cloud architecture</p>
          </AnimatedSection>
          <AnimatedSection className="team-member">
            <div className="member-photo"></div>
            <h4>Priya Sharma</h4>
            <p className="role">VP of Operations</p>
            <p>Driving excellence in delivery</p>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <AnimatedSection className="about-cta">
        <h2>Ready to Transform Your Business?</h2>
        <p>Let's discuss how LEARNEX can help you achieve your technology goals.</p>
        <motion.button 
          className="btn btn-primary"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0, 0, 255, 0.2)" }}
          whileTap={{ scale: 0.95 }}
        >
          Get in Touch
        </motion.button>
      </AnimatedSection>
    </div>
  );
}

export default AboutUs;